import { useEffect, useId, useRef, useState } from 'react';
import { CheckCircle2, CreditCard, RefreshCw, X } from 'lucide-react';
import { PaymentIcon } from './PaymentIcon';
import { PriceDiscount } from './MembershipShop';
import { userApi, type OrderPayment, type PaymentConfiguration, type PaymentMethod, type PublicPlan } from '../lib/api';
import { useI18n } from '../i18n';

export type CheckoutTarget = { plan:PublicPlan;quantity:number;method?:PaymentMethod } | { orderId:string };
export function CheckoutDialog({target,onClose,onPaid}:{target:CheckoutTarget;onClose:()=>void;onPaid:()=>void}) {
  const {t}=useI18n();const heading=useId();const dialog=useRef<HTMLDivElement>(null);
  const [config,setConfig]=useState<PaymentConfiguration|null>(null);
  const [order,setOrder]=useState<OrderPayment|null>(null);
  const [method,setMethod]=useState<PaymentMethod>('alipay');
  const [busy,setBusy]=useState(false),[loading,setLoading]=useState(true),[error,setError]=useState('');
  const idempotency=useRef(new Map<PaymentMethod,string>()),createdId=useRef<string|null>(null);
  const mounted=useRef(true),actionLock=useRef(false),paidReported=useRef(false);
  const closeRef=useRef(onClose),paidRef=useRef(onPaid);closeRef.current=onClose;paidRef.current=onPaid;
  useEffect(()=>{
    mounted.current=true;let current=true;
    const previous=document.activeElement;dialog.current?.focus();
    const oldOverflow=document.body.style.overflow;document.body.style.overflow='hidden';
    const keyboard=(event:KeyboardEvent)=>{
      if(event.key==='Escape'){event.preventDefault();closeRef.current();}
      if(event.key==='Tab'){
        const buttons=dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),a[href]');
        if(!buttons?.length)return;const first=buttons[0],last=buttons[buttons.length-1];
        if(event.shiftKey && (document.activeElement===first||document.activeElement===dialog.current)){event.preventDefault();last.focus();}
        else if(!event.shiftKey && (document.activeElement===last||document.activeElement===dialog.current)){event.preventDefault();first.focus();}
      }
    };
    document.addEventListener('keydown',keyboard);
    void Promise.all([userApi.paymentConfiguration(),'orderId' in target ? userApi.orderPayment(target.orderId) : Promise.resolve(null)])
      .then(([c,o])=>{if(current){setConfig(c);setMethod('plan' in target && target.method && c.methods.includes(target.method) ? target.method : c.methods.includes('wxpay')?'wxpay':'alipay');setOrder(o);createdId.current=o?.id??null;}})
      .catch(()=>{if(current)setError(t('无法读取支付信息，请关闭后重试。'));})
      .finally(()=>{if(current)setLoading(false);});
    return()=>{current=false;mounted.current=false;document.removeEventListener('keydown',keyboard);document.body.style.overflow=oldOverflow;if(previous instanceof HTMLElement)previous.focus();};
  },[target,t]);
  useEffect(()=>{
    if(!order?.id||order.status!=='pending'||order.receiptState==='review')return;
    let active=true,inFlight=false;const started=Date.now();
    const timer=window.setInterval(()=>{
      if(!active||inFlight||document.hidden||Date.now()-started>600000)return;
      inFlight=true;void userApi.orderPayment(order.id).then(o=>{if(active)setOrder(o);}).catch(()=>{/* Explicit query reports errors. */}).finally(()=>{inFlight=false;});
    },5000);
    return()=>{active=false;clearInterval(timer);};
  },[order?.id,order?.status,order?.receiptState]);
  useEffect(()=>{
    if(order?.status==='paid' && order.receiptState==='fulfilled' && !paidReported.current){paidReported.current=true;paidRef.current();}
  },[order]);
  const amount=order?.amountMinor ?? ('plan' in target ? Math.round(target.plan.priceCny*100)*target.quantity : 0);
  const name=order?.planName ?? ('plan' in target ? target.plan.name : t('订单'));
  const start=async()=>{
    if(actionLock.current)return;actionLock.current=true;setBusy(true);setError('');
    try{
      let id=createdId.current;
      if(!id && 'plan' in target){
        let key=idempotency.current.get(method);if(!key){key=crypto.randomUUID();idempotency.current.set(method,key);}
        const created=await userApi.createOrder(target.plan.id,target.quantity,method,key);id=created.id;createdId.current=id;
        if(mounted.current)setOrder({id:created.id,orderNo:created.orderNo,status:created.status,amountMinor:created.amountMinor,
          planName:target.plan.name,paymentMethod:method,checkoutState:null,receiptState:null,payUrl:null});
        if(created.amountMinor !== Math.round(target.plan.priceCny*100)*target.quantity){
          if(mounted.current)setError(t('订单金额已更新，请确认最新金额后继续支付。'));
          return;
        }
      }
      if(!id)return;
      const payment=await userApi.checkout(id);
      if(!mounted.current)return;setOrder(payment);
      if(payment.payUrl && payment.status==='pending' && payment.receiptState!=='review') window.location.assign(payment.payUrl);
    }catch(caught){if(mounted.current)setError(caught instanceof TypeError?t('无法连接支付服务，请查询订单后再试。'):caught instanceof Error?caught.message:t('支付申请未完成。'));}
    finally{actionLock.current=false;if(mounted.current)setBusy(false);}
  };
  const query=async()=>{
    const id=createdId.current;if(!id||actionLock.current)return;actionLock.current=true;setBusy(true);setError('');
    try{const result=await userApi.checkPayment(id);if(mounted.current)setOrder(result);}
    catch(caught){if(mounted.current)setError(caught instanceof Error && !(caught instanceof TypeError)?caught.message:t('查询失败，请稍后重试。'));}
    finally{actionLock.current=false;if(mounted.current)setBusy(false);}
  };
  const paid=order?.status==='paid'&&order.receiptState==='fulfilled';
  const review=order?.receiptState==='review';
  const manual=order?.paymentMethod==='manual';
  const uncertain=order?.checkoutState==='creating'||order?.checkoutState==='uncertain';
  const canPay=!paid&&!review&&!manual&&!uncertain&&(!order||order.status==='pending')&&config?.enabled;
  return <div className="account-dialog-backdrop" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="account-settings-dialog account-checkout-dialog" role="dialog" aria-modal="true" aria-labelledby={heading} tabIndex={-1} ref={dialog}>
      <header className="account-dialog-heading"><div><span className="account-dialog-icon"><CreditCard aria-hidden="true"/></span><h2 id={heading}>{t(paid?'支付完成':'确认订单')}</h2></div><button type="button" className="account-dialog-close" aria-label={t('关闭')} onClick={onClose}><X aria-hidden="true"/></button></header>
      <div className="account-checkout-body">
        {loading?<p role="status">{t('正在读取支付信息…')}</p>:<>
          <div className="account-checkout-summary"><span>{t(name)}{'plan' in target&&target.quantity>1?` × ${target.quantity}`:''}</span><strong><small>¥</small>{(amount/100).toFixed(2)}</strong>{!order && 'plan' in target && <PriceDiscount plan={target.plan} quantity={target.quantity}/>} {order&&<small>{t('订单编号')} {order.orderNo}</small>}</div>
          {paid?<p className="account-payment-success" role="status"><CheckCircle2 aria-hidden="true"/>{t('已确认到账，账户权益已更新。')}</p>:review?<p role="status">{t('已收到付款，订单需要人工核对。请联系支持，不要重复支付。')}</p>:manual?<p>{t('此订单由管理员确认到账，请联系支持。')}</p>:<>
            {!order&&config?.enabled&&<fieldset className="account-payment-methods"><legend>{t('支付方式')}</legend>
              {(['alipay','wxpay'] as const).filter(m=>config.methods.includes(m)).map(m=><label key={m} className={method===m?'selected':''}>
                <input type="radio" name={heading} value={m} checked={method===m} onChange={()=>setMethod(m)} disabled={busy}/>
                <PaymentIcon method={m}/><span>{t(m==='alipay'?'支付宝':'微信支付')}</span>
              </label>)}
            </fieldset>}
            {order&&<p>{t(order.paymentMethod==='wxpay'?'微信支付':'支付宝')} · {t(order.status==='cancelled'?'已取消':'待支付')}</p>}
            {!config?.enabled&&<p className="account-checkout-notice">{t(config?.reason==='not_configured'?'在线支付尚未配置，请稍后再来。':'会员购买暂未开放，请稍后再来。')}</p>}
            {uncertain&&<p>{t('支付申请结果待核对，请先查询订单，不要重复付款。')}</p>}
            {canPay&&<p className="account-checkout-hint">{t('将在安全收银台完成支付，到账结果由服务器核验。')}</p>}
          </>}
        </>}
        {error&&<p className="account-checkout-error" role="alert">{error}</p>}
        <footer className="account-dialog-actions">
          {order&&!manual&&!paid&&<button type="button" className="account-secondary-button" disabled={busy} onClick={()=>void query()}><RefreshCw aria-hidden="true"/>{t('已支付，查询结果')}</button>}
          {canPay&&<button type="button" className="account-primary-button" disabled={busy} onClick={()=>void start()}>{t(busy?'处理中…':order?'继续支付':'前往支付')}</button>}
          {!canPay&&<button type="button" className="account-secondary-button" onClick={onClose}>{t('关闭')}</button>}
        </footer>
      </div>
    </div>
  </div>;
}
